import { createContext, useCallback, useContext, useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
//To use this context you simply type useMessageItem and get state or function that you need
type MessageItemContextData = {
   formUuid: string;
   anchorTab: string | null;
   setAnchorTab: (value: any) => void;
   currentProcessStep: any | null;
   setCurrentProcessStep: (value: any) => void;
   instanceId: number;
   stepId: string;
   refreshForm: () => void;
   formRefresher: boolean;
   formRef: any;
   formContextData: any;
   setFormContextData: (data: any) => void;
   isAnyFieldInFormLoading: boolean;
   setIsAnyFieldInFormLoading: (value: any) => void;
   isAnyRowEditing: boolean;
   setIsAnyRowEditing: (value: boolean) => void;
   requiredLock: any;
   setRequiredLock: (data: any) => void;
   alertLock: any;
   setAlertLock: (data: any) => void;
   isButtonClicked: boolean;
   setIsButtonClicked: (value: boolean) => void;
   triggerRequiredFieldsModal: boolean;
   setTriggerRequiredFieldsModal: (value: boolean) => void;
   pendingAction: (() => void) | null;
   setPendingAction: (action: (() => void) | null) => void;
   missingCheckboxSelections: Set<string>;
   updateCheckboxSelection: (techName: string, hasSelection: boolean) => void;
};

const MessageItemContext = createContext<MessageItemContextData>({
   formUuid: "",
   anchorTab: null,
   setAnchorTab: () => {},
   currentProcessStep: null,
   setCurrentProcessStep: () => {},
   instanceId: 0,
   stepId: "",
   refreshForm: () => {},
   formRefresher: false,
   formRef: null,
   formContextData: "",
   setFormContextData: () => {},
   isAnyFieldInFormLoading: false,
   setIsAnyFieldInFormLoading: () => {},
   isAnyRowEditing: false,
   setIsAnyRowEditing: () => {},
   requiredLock: false,
   setRequiredLock: () => {},
   alertLock: false,
   setAlertLock: () => {},
   isButtonClicked: false,
   setIsButtonClicked: () => {},
   triggerRequiredFieldsModal: false,
   setTriggerRequiredFieldsModal: () => {},
   pendingAction: null,
   setPendingAction: () => {},
   missingCheckboxSelections: new Set(),
   updateCheckboxSelection: () => {},
});

const MessageItemProvider = ({ children, formRef, currentProcessStep, setCurrentProcessStep }) => {
   const formUuid = useRef(uuidv4());
   const [anchorTab, setAnchorTab] = useState(null);
   const [formRefresher, setFormRefresher] = useState(false);
   const [requiredLock, setRequiredLock] = useState(false);
   const [alertLock, setAlertLock] = useState(false);
   const [formContextData, setFormContextData] = useState(null);
   const [isAnyFieldInFormLoading, setIsAnyFieldInFormLoading] = useState(false);
   const [isAnyRowEditing, setIsAnyRowEditing] = useState<boolean>(false);

   const [triggerRequiredFieldsModal, setTriggerRequiredFieldsModal] = useState<boolean>(false);
   const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
   const [missingCheckboxSelections, setMissingCheckboxSelections] = useState<Set<string>>(
      new Set(),
   );

   const updateCheckboxSelection = useCallback((techName: string, hasSelection: boolean) => {
      setMissingCheckboxSelections((prev) => {
         const alreadyMissing = prev.has(techName);
         if (hasSelection ? !alreadyMissing : alreadyMissing) return prev;
         const next = new Set(prev);
         if (hasSelection) next.delete(techName);
         else next.add(techName);
         return next;
      });
   }, []);

   //state used to block user from clicing anothe time in action button when step is being executed (pending)
   const [isButtonClicked, setIsButtonClicked] = useState(false);

   const refreshForm = () => {
      setFormRefresher(!formRefresher);
   };

   return (
      <MessageItemContext.Provider
         value={{
            formRef,
            formUuid: formUuid.current,
            anchorTab,
            setAnchorTab,
            currentProcessStep,
            setCurrentProcessStep,
            instanceId: currentProcessStep?.["instanceId"] || 0,
            stepId: currentProcessStep?.["stepId"] || "",
            refreshForm,
            formRefresher,
            formContextData,
            setFormContextData,
            isAnyFieldInFormLoading,
            setIsAnyFieldInFormLoading,
            isAnyRowEditing,
            setIsAnyRowEditing,
            requiredLock,
            setRequiredLock,
            alertLock,
            setAlertLock,
            isButtonClicked,
            setIsButtonClicked,
            triggerRequiredFieldsModal,
            setTriggerRequiredFieldsModal,
            pendingAction,
            setPendingAction,
            missingCheckboxSelections,
            updateCheckboxSelection,
         }}
      >
         {children}
      </MessageItemContext.Provider>
   );
};

export default MessageItemProvider;

export const useMessageItem = () => {
   const context = useContext(MessageItemContext);
   if (!context)
      throw new Error(
         "You are trying to use Message Item Context outside the Message Item Provider",
      );
   return context;
};
